import { ButtonHeader } from '../../../Header/Style';
import SVG from '../../../SVG';
import { Control, ControlContainer } from '../../../Controls/styled';
import {
  downloadExcel,
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import Table from '../../../Table';
import useExportsController from '../useExportsController';
import { formatBRL } from '../../../../store/masks';
import * as XLSX from 'xlsx';
import { useAbility } from '../../../Hooks/useAbility';
import { useState } from 'react';

const useDepositsController = () => {
  const {
    getInvoice,
    setInvoice,
    autocompleteDeposit,
    filterDeposit,
    deleteDeposits: deleteDepositsAction,
    createDeposit,
    updateDeposit,
    validateForm,
    filterSelectCompanies,
    setModal,
    getFormMessage,
    currentRoute,
    enqueueSnackbar,
    selectCompanies,
    createDeposits,
    getDepositsFormMessage,
    validateDepositsForm,
    filterDailyDeposit,
    filterCompleteDeposit,
    updateDeposits,
    updateLockDeposit,
    filterGroupDeposit,
    groups,
    user,
    selectedGroupId,
  } = useExportsController();
  const { Can } = useAbility();

  const deposits = getInvoice('filters.deposits.data.data');
  const dailyDeposits = getInvoice('filters.dailyDeposits.data.data');
  const checkDepositIds = getInvoice('selecteds.deposits');
  const reportDeposit = getInvoice('filters.deposits.data.report');
  const reportDailyDeposit = getInvoice('filters.dailyDeposits.data.report');

  const setCheckDepositIds = (selectedDepositIds: number[]) =>
    setInvoice('selecteds.deposits', selectedDepositIds);

  const allDepositChecks = deposits.every(({ id }) =>
    checkDepositIds.includes(id),
  );

  const depositCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckDepositIds(deposits.map(({ id }) => id));
    } else {
      setCheckDepositIds([]);
    }
  };

  const checkDepositId = (depositId: number) => {
    const newcheckIds = checkDepositIds.includes(depositId)
      ? checkDepositIds.filter((id) => id !== depositId)
      : [...checkDepositIds, depositId];
    setCheckDepositIds(newcheckIds);
  };

  const deleteDepositLoadings = getInvoice('loadings.deleteDeposits');

  const setDeleteDepositLoadings = (setDeleteDepositLoadings: boolean[]) =>
    setInvoice('loadings.deleteDeposits', setDeleteDepositLoadings);

  const deleteDeposits = (depositIds: number[] = checkDepositIds) =>
    new Promise((resolve, reject) => {
      const length = depositIds.length;

      const indexes = depositIds.map((depositId) =>
        deposits.findIndex(({ id }) => depositId === id),
      );

      setDeleteDepositLoadings(
        deposits.map((_, i) =>
          indexes.includes(i) ? true : deleteDepositLoadings[i],
        ),
      );

      deleteDepositsAction(depositIds)
        .then(() => {
          const newCheckIds = checkDepositIds.filter(
            (id) => !depositIds.includes(id),
          );
          setCheckDepositIds(newCheckIds);
          filterDeposit();
          resolve(true);
          enqueueSnackbar(
            length === 1
              ? 'O depósito foi excluído com sucesso'
              : 'Os depósitos foram excluídos com sucesso',
            {
              variant: 'success',
            },
          );
        })
        .catch(() => {
          reject(false);
          enqueueSnackbar(
            length === 1
              ? 'Houve uma falha ao excluir o depósito'
              : 'Houve uma falha ao excluir os depósitos',
            {
              variant: 'error',
            },
          );
        })
        .finally(() => {
          setDeleteDepositLoadings(
            deposits.map((_, i) =>
              indexes.includes(i) ? false : deleteDepositLoadings[i],
            ),
          );
        });
    });

  const editDeposit = ({
    id,
    date,
    reference,
    value,
    company,
    group,
    locked,
  }: Deposit) => {
    setModal('single.updateDeposit', true);
    setInvoice('forms.deposits.update', {
      id,
      date,
      reference,
      value,
      locked,
      company: {
        id: company.id,
        name: company.name,
      },
      group: {
        id: group?.id ?? 0,
        number: group?.number ?? 0,
        name: group?.name ?? ''
      },
    });
  };

  const searchFilter = getInvoice('filters.deposits.filter');
  const dailySearchFilter = getInvoice('filters.dailyDeposits.filter');

  const companyFilter = getInvoice('filters.selectCompanies.filter');

  const setSearchFilter = (filter: SearchFilter) =>
    setInvoice('filters.deposits.filter', filter);

  const setDailySearchFilter = (filter: SearchFilter) =>
    setInvoice('filters.dailyDeposits.filter', filter);

  const setCompanyFilter = (filter: SearchFilter) =>
    setInvoice('filters.selectCompanies.filter', filter);

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(field, e.target.value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const setCompanyFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(
      field,
      e.target.value,
      companyFilter,
      operator,
    );
    setCompanyFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getSortDailyField = (field: string) =>
    getSortColumn(field, dailySearchFilter);

  const setSortDailyField = (field: string) => (value) => {
    setDailySearchFilter(setSortColumn(field, value, dailySearchFilter));
    filterDailyDeposit();
  };

  const getAutocompleteItems = <
    T extends keyof InvoiceState['autocompletes']['deposits'],
  >(
    field: T,
  ) =>
    getInvoice(`autocompletes.deposits.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof InvoiceState['autocompletes']['deposits'],
  ) => {
    return autocompleteDeposit(field);
  };

  const setAutocompleteFilterField =
    (field: keyof InvoiceState['autocompletes']['deposits']) => (e) => {
      setInvoice(
        `autocompletes.deposits.${field}.filter.value`,
        e.target.value,
      );
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const depositId = deposits[index].id;
    checkDepositId(depositId);
  };

  const hasCheckedDeposits = checkDepositIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setInvoice('filters.deposits.filter', {
      ...getInvoice('filters.deposits.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setInvoice('selecteds.deposits', []);
  };

  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownload = () => {
    setDownloadLoading(true);
    filterCompleteDeposit()
      .then(([depositResult, dailyResult]) => {
        const deposits = depositResult.data.filterDeposit.data;
        const dailyDeposits = dailyResult.data.filterDailyDeposit.data;
        const data = [
          ...deposits.map(
            ({ company, date, reference, value, createdAt, updatedAt }) => ({
              Valor: formatBRL(value),
              Companhia: company.name,
              'Data do depósito': new Date(date)
                .toLocaleString('pt-BR')
                .split(',')[0],
              'Data de referência': new Date(reference)
                .toLocaleString('pt-BR')
                .split(',')[0],
              'Criado em': new Date(createdAt).toLocaleString('pt-BR'),
              'Atualizado em': new Date(updatedAt).toLocaleString('pt-BR'),
            }),
          ),
          {
            Valor: formatBRL(reportDeposit.total),
            Companhia: '',
            'Data do depósito': '',
            'Data de referência': '',
            'Criado em': '',
            'Atualizado em': '',
          },
        ];

        const dailyData = [
          ...dailyDeposits.map(({ value, reference, quantity }) => ({
            Valor: formatBRL(value),
            Referência: new Date(reference)
              .toLocaleString('pt-BR')
              .split(',')[0],
            Quantidade: quantity,
          })),
          {
            Valor: formatBRL(reportDailyDeposit.total),
            Referência: '',
            Quantidade: reportDailyDeposit.quantity,
          },
        ];

        const worksheetDeposits = XLSX.utils.json_to_sheet(data);
        const worksheetDailyDeposits = XLSX.utils.json_to_sheet(dailyData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheetDeposits, 'Depósitos');
        XLSX.utils.book_append_sheet(
          workbook,
          worksheetDailyDeposits,
          'Depósitos diários',
        );

        const filename = `depositos_${new Date().toLocaleString('pt-BR').replaceAll('/', '-').replaceAll(':', '-').replaceAll(', ', '_')}.xlsx`;
        downloadExcel(workbook, filename);
        enqueueSnackbar('Depósitos baixados com sucesso', {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao baixar os depósitos', {
          variant: 'error',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const isLock = !checkDepositIds.some(
    (checkDepositId) =>
      deposits.find(({ id }) => id === checkDepositId)?.locked,
  );

  const [lockLoading, setLockLoading] = useState(false);
  const lockDeposits = () => {
    setLockLoading(true);
    const length = checkDepositIds.length;
    updateLockDeposit(isLock)
      .then(() => {
        setInvoice(
          'filters.deposits.data.data',
          deposits.map((deposit) => ({
            ...deposit,
            locked: checkDepositIds.includes(deposit.id)
              ? isLock
              : deposit.locked,
          })),
        );

        filterDeposit();
        enqueueSnackbar(
          length === 1
            ? `Depósito ${isLock ? 'bloqueado' : 'desbloqueado'} com sucesso`
            : `Depósitos ${isLock ? 'bloqueados' : 'desbloqueados'} com sucesso`,
          {
            variant: 'success',
          },
        );
      })
      .catch(() => {
        enqueueSnackbar(
          length === 1
            ? `Houve uma falha ao ${isLock ? 'bloquear' : 'desbloquear'} o depósito`
            : `Houve uma falha ao ${isLock ? 'bloquear' : 'desbloquear'} os depósitos`,
          {
            variant: 'error',
          },
        );
      })
      .finally(() => {
        setLockLoading(false);
      });
  };

  const isEdition =
    checkDepositIds.length > 0 &&
    !checkDepositIds.some((checkDepositId) =>
      deposits.find(({ id, locked }) => locked && checkDepositId === id),
    );

  const DepositControls = () => {
    return currentRoute.includes('/tools/exports/deposits') ? (
      <>
        <Can I="create" a="Deposit">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createDeposit', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVO DEPÓSITO
            </ButtonHeader>
          )}
        </Can>
        <Can I="update" a="Deposit" field="locked">
          {() =>
            checkDepositIds.length > 0 ? (
              <ButtonHeader
                className="button-header"
                onClick={lockDeposits}
                disabled={lockLoading}
              >
                <div
                  style={{
                    marginRight: '10px',
                  }}
                >
                  {lockLoading ? (
                    <SVG.Icon.Loader width={25} height={25} />
                  ) : isLock ? (
                    <SVG.Icon.Lock width={25} height={25} />
                  ) : (
                    <SVG.Icon.Unlock />
                  )}
                </div>
                {isLock ? 'BLOQUEAR' : 'DESBLOQUEAR'}
              </ButtonHeader>
            ) : null
          }
        </Can>
        <Can I="update" a="Deposit">
          {() =>
            isEdition ? (
              <ButtonHeader
                className="button-header"
                onClick={() => {
                  setModal('single.updateManyDeposits', true);
                }}
              >
                <div
                  style={{
                    marginRight: '10px',
                  }}
                >
                  <SVG.Icon.Edit width={25} height={25} />
                </div>
                EDITAR
              </ButtonHeader>
            ) : null
          }
        </Can>
        <ControlContainer>
          {!downloadLoading ? (
            <Control onClick={handleDownload}>
              <SVG.Icon.Download color="#364856" width={24} height={24} />
            </Control>
          ) : (
            <Control>
              <SVG.Icon.Loader width={24} height={24} />
            </Control>
          )}

          <Can I="delete" a="Deposit">
            {() => (
              <Control
                active={hasCheckedDeposits}
                disabled={!hasCheckedDeposits}
                onClick={() => {
                  setModal('single.deleteDeposits', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedDeposits ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>

          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterDeposit', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };

  const total = getInvoice('filters.deposits.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };

  const dailyTotal = getInvoice('filters.dailyDeposits.data.meta.total');
  const dailyCurrentPage = dailySearchFilter.page ?? 1;
  const dailyItemsPerPage = dailySearchFilter.limit ?? 0;
  const setDailyPage = (page: number) => {
    setDailySearchFilter({
      ...dailySearchFilter,
      page,
    });
    filterDailyDeposit();
  };

  const setDailyItemsPerPage = (limit: number) => {
    setDailySearchFilter({
      ...dailySearchFilter,
      page: 1,
      limit,
    });
    filterDailyDeposit();
  };

  const DailyTableControl = () => {
    return (
      <Table.Control
        currentPage={dailyCurrentPage}
        itemsPerPage={dailyItemsPerPage}
        totalOfItems={dailyTotal}
        setPage={setDailyPage}
        setItemsPerPage={setDailyItemsPerPage}
      />
    );
  };

  const depositGroups = groups.deposit;

  const depositGroupItems = depositGroups.map((group) => ({
    id: group.id,
    name: `${group.number} - ${group.name}`,
    data: group,
  }));

  return {
    deposits,
    checkDepositIds,
    setCheckDepositIds,
    allDepositChecks,
    depositCheckAll,
    checkDepositId,
    deleteDeposits,
    deleteDepositLoadings,
    DepositControls,
    editDeposit,
    searchFilter,
    setSearchFilter,
    getFilterField,
    setFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setAutocompleteFilterField,
    setField,
    onLineClickHandler,
    filterDeposit,
    setInvoice,
    TableControl,
    getFormMessage,
    createDeposit,
    updateDeposit,
    getInvoice,
    setModal,
    validateForm,
    filterSelectCompanies,
    setCompanyFilterField,
    selectCompanies,
    reportDeposit,
    createDeposits,
    getDepositsFormMessage,
    validateDepositsForm,
    dailyDeposits,
    reportDailyDeposit,
    DailyTableControl,
    getSortDailyField,
    setSortDailyField,
    filterDailyDeposit,
    updateDeposits,
    filterGroupDeposit,
    groups,
    user,
    depositGroupItems,
    depositGroups,
    selectedGroupId,
  };
};

export default useDepositsController;
